import { Injectable, inject } from '@angular/core';
import { NgxCsvParser } from 'ngx-csv-parser';
import { firstValueFrom } from 'rxjs';
import { TableFileService } from './table-file.service';
import type { WorkbookSheet } from './workbook-reader';
import { csvObjectsToSheet, fileBaseName, UploadSourceFile, uploadFileKind } from './workbook-sheets';

/**
 * Reads the files of a multi-file table upload into sheets: a CSV becomes one sheet (parsed and
 * accounting-cleaned exactly as a single CSV upload is), an Excel workbook every one of its
 * sheets. Files are read one after another so a large selection never holds two parses at once.
 * A file that cannot be read is returned with `error` set rather than failing the whole batch.
 */
@Injectable({ providedIn: 'root' })
export class UploadFilesReaderService {
    private readonly csvParser = inject(NgxCsvParser);
    private readonly fileService = inject(TableFileService);

    async readFiles(files: File[]): Promise<UploadSourceFile[]> {
        const out: UploadSourceFile[] = [];
        for (const file of files) {
            out.push(await this.readFile(file));
        }
        return out;
    }

    private async readFile(file: File): Promise<UploadSourceFile> {
        const kind = uploadFileKind(file);
        const base = {
            fileName: file.name,
            kind: kind ?? 'csv',
            sizeKB: +(file.size / 1024).toFixed(2),
        };
        if (!kind) {
            return { ...base, sheets: [], error: 'Unsupported file type' };
        }
        try {
            const sheets = kind === 'csv' ? [await this.readCsv(file)] : await this.readWorkbook(file);
            return { ...base, sheets, error: null };
        } catch (e: unknown) {
            const message = e instanceof Error ? e.message : String(e ?? '');
            return { ...base, sheets: [], error: message || 'The file could not be read.' };
        }
    }

    private async readCsv(file: File): Promise<WorkbookSheet> {
        const parsed = await firstValueFrom(
            this.csvParser.parse(file, { header: true, delimiter: ',', encoding: 'utf8' })
        );
        if (!Array.isArray(parsed)) {
            throw new Error('CSV parse error');
        }
        const rows = this.fileService.processCSVDataForAccountingFormat(parsed) as Record<string, unknown>[];
        return csvObjectsToSheet(fileBaseName(file.name), rows);
    }

    private readWorkbook(file: File): Promise<WorkbookSheet[]> {
        if (typeof Worker === 'undefined') {
            return Promise.reject(new Error('Worker unavailable'));
        }
        return new Promise((resolve, reject) => {
            const worker = new Worker(new URL('../file-parser.worker.ts', import.meta.url));
            const done = () => worker.terminate();
            worker.onmessage = ({ data }) => {
                done();
                if (Array.isArray(data?.sheets)) {
                    resolve(data.sheets as WorkbookSheet[]);
                } else {
                    reject(new Error(data?.error || 'Error parsing Excel file.'));
                }
            };
            worker.onerror = () => {
                done();
                reject(new Error('Error parsing Excel file.'));
            };
            worker.postMessage({ file, listAll: true });
        });
    }
}
